import { useEffect, useRef, useState } from 'react'

/**
 * Where a list was left — the page, the search and the filters — so that opening a
 * record and coming back lands on the same view.
 *
 * A paginated list is a place: page 8 of the users, filtered to one department,
 * searching for "ahmed". Opening a row and pressing Back used to return to page 1 of
 * an unfiltered list, and the work of finding the row had to be done again for the
 * next one — which is most of what an administrator does on these screens.
 *
 * IN sessionStorage, per browser tab. It began as a module-level Map, which is enough
 * for a click-and-Back inside the running app and is NOT enough for anything that
 * reloads the document: a details page opened in a fresh tab, a hard refresh, a return
 * through a server-rendered screen. Those all restart the module and the Map with it,
 * and the reader lands on page 1 again. The tab is the right lifetime — it ends when
 * the tab does, and it is not shared with another window looking at another list.
 *
 * NOT IN THE URL, though: these are working positions rather than addresses to share,
 * and putting them there would rewrite history on every keystroke in a search box.
 *
 * Values must survive `JSON.stringify` — page numbers, search text, filter objects,
 * `{key, dir}` sorts. That is everything the lists keep here.
 */
const STORE = 'rawam.list-state'

/** The parsed store, so a keystroke does not re-parse the whole thing. */
let cache = null

function readAll() {
  if (cache) return cache
  try {
    cache = JSON.parse(sessionStorage.getItem(STORE) ?? '{}') ?? {}
  } catch {
    /* A browser that refuses storage — or a store someone else corrupted — is a browser
       without a remembered position, not a broken screen. */
    cache = {}
  }
  return cache
}

function writeAll(next) {
  cache = next
  try {
    sessionStorage.setItem(STORE, JSON.stringify(next))
  } catch {
    /* Private mode, or a full quota: the in-memory mirror still serves this session. */
  }
}

/** Forget one list's position, or all of them — a sign-out drops everything. */
export function forgetListState(key) {
  if (key === undefined) {
    writeAll({})
    return
  }
  const all = { ...readAll() }
  delete all[key]
  writeAll(all)
}

/**
 * One value, read or written straight away — for what is set in the same breath as
 * leaving the screen. `useRemembered` writes from an effect, and a click that also
 * navigates unmounts the component before that effect can run.
 */
export function recall(key) {
  return readAll()[key]
}

export function remember(key, value) {
  writeAll({ ...readAll(), [key]: value })
}

/**
 * `useState`, remembered under `key` for as long as the browser tab lives.
 *
 * A NULL KEY REMEMBERS NOTHING, and it is plain `useState` again. That is what a
 * shared component gets when its screen has opted out — without it every unkeyed table
 * in the app would share one position and restore each other's.
 *
 * @param {string|null} key  unique per list and per value — `users:filter`
 * @param {*} initialValue   used the first time this list is opened
 */
export function useRemembered(key, initialValue) {
  const stored = (forKey) => {
    if (forKey == null) return initialValue
    const all = readAll()
    return forKey in all ? all[forKey] : initialValue
  }

  const [value, setValue] = useState(() => stored(key))

  /* A KEY THAT CHANGES IS A DIFFERENT LIST. The per-tab and per-record keys — the points
     log's tab, one campaign's report — change on a component that never unmounts, and a
     `useState` initializer runs once: without this the value from the old key stays on
     screen and is then written over the new key's own. */
  const lastKey = useRef(key)
  if (key !== lastKey.current) {
    lastKey.current = key
    setValue(stored(key))
  }

  useEffect(() => {
    if (key == null) return
    const all = readAll()
    if (all[key] === value) return
    writeAll({ ...all, [key]: value })
  }, [key, value])

  return [value, setValue]
}

/**
 * An effect that does NOT run on the first render.
 *
 * The lists reset themselves to page 1 whenever the search or a filter changes, and
 * that effect fires once on mount too — which would throw away the remembered page the
 * moment it was restored. What it means is "when this CHANGES", and that is what this
 * runs.
 */
export function useOnChange(effect, deps) {
  /* COMPARED, NOT COUNTED. A "skip the first render" flag held in a ref is wrong under
     StrictMode, which mounts, unmounts and mounts again while the ref survives — the
     second pass then looks like a change and resets the page the screen had just
     restored. What "changed" means is that a dependency differs, so that is what is
     asked. */
  const previous = useRef(deps)

  useEffect(() => {
    const changed =
      previous.current.length !== deps.length ||
      deps.some((value, index) => !Object.is(value, previous.current[index]))
    previous.current = deps
    if (!changed) return undefined
    return effect()
    // The caller's deps are the contract, exactly as with useEffect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)
}
